import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { ReviewCard } from "@/components/ReviewsSection";
import { Stars } from "@/components/Stars";
import { API, SERVICE_OPTIONS } from "@/constants/site";

const inputCls =
  "w-full px-4 py-3 rounded-lg bg-black/40 border border-white/10 text-white text-xs focus:outline-none focus:border-pink-500 placeholder:text-slate-500";
const labelCls = "block text-xs font-mono uppercase tracking-wider text-slate-400 mb-2";

const ReviewsPage = () => {
  const [data, setData] = useState({ reviews: [], count: 0, average: 0 });
  const [form, setForm] = useState({ name: "", service: SERVICE_OPTIONS[0], rating: 5, text: "" });
  const [sending, setSending] = useState(false);

  const load = () =>
    axios
      .get(`${API}/reviews`)
      .then((res) => setData(res.data))
      .catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, form);
      toast.success("Thank you — your review is now live.");
      setForm({ name: "", service: SERVICE_OPTIONS[0], rating: 5, text: "" });
      load();
    } catch (err) {
      toast.error("Could not submit your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-[#110e1a] text-[#f8fafc] min-h-screen antialiased overflow-x-hidden">
      <Navbar />

      <section className="pt-16 pb-12 px-6 text-center border-b border-white/5" data-testid="reviews-hero">
        <div className="text-amber-400 text-sm mb-2">&#9733;&#9733;&#9733;&#9733;&#9733; CLIENT RANKINGS</div>
        <h1 className="font-poster text-5xl sm:text-6xl uppercase tracking-normal text-white mb-4">
          <span className="ln"><span>Reviews &amp; Ratings</span></span>
        </h1>
        {data.count > 0 && (
          <div className="fade-sub flex items-center justify-center gap-3" data-testid="rating-summary">
            <span className="font-poster text-5xl text-white" data-testid="rating-average">{data.average}</span>
            <Stars rating={Math.round(data.average)} size={22} />
            <span className="text-xs font-mono text-slate-400" data-testid="rating-count">
              {data.count} verified reviews
            </span>
          </div>
        )}
      </section>

      <section className="py-16 px-6" data-testid="reviews-list">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {data.reviews.map((r) => (
            <ReviewCard key={r.id} review={r} />
          ))}
        </div>
      </section>

      <section className="py-16 px-6 border-t border-white/5 bg-[#09070e]" data-testid="review-form-section">
        <div className="max-w-2xl mx-auto">
          <span className="text-xs font-mono uppercase tracking-widest text-pink-400">Share Your Experience</span>
          <h2 className="font-poster text-3xl uppercase text-white mt-2 mb-6">Leave A Review</h2>
          <form
            onSubmit={submit}
            data-testid="review-form"
            className="p-8 rounded-2xl border border-white/10 bg-[#171324] space-y-4"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls} htmlFor="review-name">Name</label>
                <input
                  id="review-name"
                  data-testid="review-name-input"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className={inputCls}
                  placeholder="Your Name"
                />
              </div>
              <div>
                <label className={labelCls} htmlFor="review-service">Service Used</label>
                <select
                  id="review-service"
                  data-testid="review-service-select"
                  value={form.service}
                  onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg bg-[#201a33] border border-white/10 text-white text-xs focus:outline-none focus:border-pink-500"
                >
                  {SERVICE_OPTIONS.map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <span className={labelCls}>Your Rating</span>
              <div className="flex gap-2" data-testid="review-rating-input">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    data-testid={`review-rating-${n}`}
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    className="p-1 transition-transform hover:scale-110"
                    aria-label={`${n} star`}
                  >
                    <Star
                      size={26}
                      className={n <= form.rating ? "fill-amber-400 text-amber-400" : "text-slate-600"}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className={labelCls} htmlFor="review-text">Your Review</label>
              <textarea
                id="review-text"
                data-testid="review-text-input"
                rows={3}
                required
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
                className={inputCls}
                placeholder="How was the work, communication and turnaround?"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="review-submit-btn"
              className="w-full py-4 rounded-xl bg-pink-500 hover:bg-pink-600 disabled:opacity-60 text-white font-bold text-xs uppercase font-mono tracking-wider transition-colors shadow-lg shadow-pink-500/25"
            >
              {sending ? "Submitting..." : "Submit Review"}
            </button>
          </form>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default ReviewsPage;
